import * as React from "react"
import { groupForPhone } from "./phone"
import type { WorkPage as Data, Theme, Case } from "./types"
import { ModuleView } from "./Modules"
import { startWorkPage } from "./controller"
import { Sections } from "./sections"
import { WORK_PAGES } from "./pages"
import { useShell } from "../shell/context"
import { MENU } from "../shell/settings"
import "./work.css"

const varsOf = (t: Theme) =>
    ({
        "--w-page": t.page,
        "--w-gradient": t.gradient,
        "--w-fill": t.fill,
        "--w-line": t.line,
        "--w-title": t.title,
        "--w-body": t.body,
    }) as React.CSSProperties

const pageStyle = (t: Theme) => {
    const st = document.getElementById("nto-page-style")
    if (st) st.textContent = `html body { background: ${t.page}; }`
}

/** the backgrounds' crossfade curve (ease in-out) */
const BG_EASE = (() => {
    const x1 = 0.45, y1 = 0, x2 = 0.55, y2 = 1
    const bx = (t: number) => 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t
    const by = (t: number) => 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
    return (x: number) => {
        let lo = 0,
            hi = 1
        for (let k = 0; k < 20; k++) {
            const m = (lo + hi) / 2
            if (bx(m) < x) lo = m
            else hi = m
        }
        return by((lo + hi) / 2)
    }
})()

/** the phone layout (as in work.css: up to 1279.98 wide) */
const PHONE = "(max-width: 1279.98px)"
function usePhone() {
    const [on, set] = React.useState(() => matchMedia(PHONE).matches)
    React.useEffect(() => {
        const q = matchMedia(PHONE)
        const ch = () => set(q.matches)
        q.addEventListener("change", ch)
        return () => q.removeEventListener("change", ch)
    }, [])
    return on
}

/** Every category with cases, in menu order. */
const LIVE = () => MENU.categories.map((c) => WORK_PAGES[c.id]).filter(Boolean)

/** A work page: its cases one after the other. Each case has its own colours;
 *  the background, header, work navigation and browser bars take the colours
 *  of the case on screen (the background crossfades).
 *
 *  All categories are on it, in menu order: it opens at the one chosen, and
 *  scrolling on (or back up) carries into the next (or the one before), like
 *  into the next case. The category on screen is the one shown: its address,
 *  its menu entry, its dots. Nothing is added, taken away or rebuilt, so
 *  nothing moves, loads or plays twice. No going round from the last to the
 *  first: the page has a start and an end. */
type Item = { feed: number; page: Data; c: Case; ci: number }

export default function WorkPage({ page: start }: { page: Data }) {
    const shell = useShell()
    // every category, in menu order, one after the other: opened at the one
    // chosen, the ones before it above. The first has nothing above it and the
    // last nothing below (no going round, so you can't get lost).
    const feed = React.useMemo(() => {
        const l = LIVE()
        return l.some((p) => p.id === start.id) ? l : [start]
    }, [start])
    const origin = feed.findIndex((p) => p.id === start.id)
    // phones: a case's single full-screen images brought together (phone.ts)
    const phone = usePhone()
    const items: Item[] = React.useMemo(() => {
        const out: Item[] = []
        feed.forEach((p, f) => p.cases.forEach((c, ci) => out.push({ feed: f, page: p, c: phone ? { ...c, modules: groupForPhone(c.modules) } : { ...c, modules: c.modules.filter((m) => !(m.type === "grid" && m.phoneOnly)) }, ci })))
        return out
    }, [feed, phone])
    const keyOf = (it: Item) => `${it.feed}:${it.page.id}:${it.ci}`
    const first = start.cases[0].theme
    const startCase = items.findIndex((it) => it.feed === origin)
    const [active, setActive] = React.useState(startCase)
    const activeRef = React.useRef(startCase)
    const shownRef = React.useRef(`${origin}:${start.id}`)

    // The page colour behind everything (and behind Safari's bars).
    React.useLayoutEffect(() => pageStyle(first), [start])

    // Sections and their scrolling/entrances: started before the first paint, so
    // the modules already sit at the start of their entrance.
    const root = React.useRef<HTMLDivElement>(null)
    const mark = () => {
        const list = Array.from(root.current!.querySelectorAll<HTMLElement>("[data-nto-scene]"))
        // every case starts a new dot in the section indicator
        root.current!.querySelectorAll<HTMLElement>(".work-case").forEach((c) => {
            const fp = c.dataset.feedPage || ""
            c.querySelectorAll<HTMLElement>("[data-nto-scene]").forEach((s) => (s.dataset.feedPage = fp))
            const s = c.querySelector("[data-nto-scene]")
            if (!s) return
            s.setAttribute("data-nto-client-start", "")
        })
        return list
    }
    React.useLayoutEffect(() => {
        const list = mark()
        Sections.current = shownRef.current
        Sections.set(list)
        // opened at the category chosen (the ones before it sit above)
        const o = root.current!.querySelector<HTMLElement>("[data-nto-origin] [data-nto-scene]") || undefined
        if (o) {
            const top = Math.max(0, o.getBoundingClientRect().top + scrollY)
            try {
                scrollTo({ top, left: 0, behavior: "instant" as ScrollBehavior })
            } catch {
                scrollTo(0, top)
            }
        }
        const stop = startWorkPage(list, undefined, undefined, o)
        return () => {
            stop()
            Sections.clear(Sections.get())
            // (here, not later: a work page replacing this one has set its own by
            // then, and clearing it would give the indicator every section)
            if (Sections.current === shownRef.current) Sections.current = ""
        }
    }, [])

    // The background of the case on screen fades in over the one before. Done
    // here frame by frame (not as a CSS transition), so the browser bars can be
    // handed the very same progress: one frame ahead, as Safari shows a bar
    // colour a frame late (src/app/bars.ts).
    const fade = React.useRef({ raf: 0, ahead: null as number | null })
    const fadeIn = (i: number) => {
        const f = fade.current
        cancelAnimationFrame(f.raf)
        const layers = Array.from(root.current?.querySelectorAll<HTMLElement>(".work-bg") || [])
        const el = layers[i]
        if (!el) return undefined
        const from = Number(getComputedStyle(el).opacity) || 0
        // held where they are now (the class change that follows mustn't move them)
        layers.forEach((l, j) => {
            l.style.opacity = getComputedStyle(l).opacity
            l.style.transition = "none"
            l.style.zIndex = j === i ? "1" : "0"
        })
        const ms = 600,
            t0 = performance.now()
        let frame = 1000 / 60,
            last = 0
        const at = (t: number) => from + (1 - from) * BG_EASE(Math.min(1, Math.max(0, (t - t0) / ms)))
        f.ahead = from
        const step = (t: number) => {
            if (last) frame += (Math.min(50, Math.max(4, t - last)) - frame) * 0.2
            last = t
            el.style.opacity = String(at(t))
            if (t - t0 < ms) {
                f.ahead = at(t + frame)
                f.raf = requestAnimationFrame(step)
            } else {
                f.ahead = null
                layers.forEach((l, j) => (l.style.opacity = j === i ? "1" : "0"))
            }
        }
        f.raf = requestAnimationFrame(step)
        return () => fade.current.ahead
    }
    React.useEffect(() => () => cancelAnimationFrame(fade.current.raf), [])

    // The case and category on screen.
    React.useEffect(() => {
        let raf = 0
        const check = () => {
            raf = 0
            const el = Sections.onScreen()
            const i = el ? Number(el.closest<HTMLElement>("[data-case]")?.dataset.case || 0) : activeRef.current
            if (i !== activeRef.current && items[i]) {
                activeRef.current = i
                setActive(i)
                const t = items[i].c.theme
                pageStyle(t)
                shell?.caseColors(t.tone, t.stops, fadeIn(i))
            }
            // (a page change under way owns the page)
            if (Sections.leaving) return
            const it = items[activeRef.current]
            if (!it) return
            const id = `${it.feed}:${it.page.id}`
            if (id !== shownRef.current) {
                const forward = it.feed > Number(shownRef.current.split(":")[0])
                shownRef.current = id
                Sections.setCurrent(id)
                shell?.continueTo(it.page.path, !forward)
            }
        }
        const queue = () => raf || (raf = requestAnimationFrame(check))
        addEventListener("scroll", queue, { passive: true })
        addEventListener("resize", queue)
        // and every 150ms, whatever moved the page (a jump, a restored position)
        const timer = setInterval(queue, 150)
        queue()
        return () => {
            clearInterval(timer)
            cancelAnimationFrame(raf)
            removeEventListener("scroll", queue)
            removeEventListener("resize", queue)
        }
    }, [items])

    return (
        <div ref={root} className="work" data-page={start.id} style={varsOf(first)}>
            {items.map((it, i) => (
                <div key={"bg:" + keyOf(it)} className="work-bg" data-on={i === active} style={{ background: it.c.theme.gradient }} />
            ))}
            {items.map((it, i) => (
                <div
                    key={keyOf(it)}
                    className="work-case"
                    data-case={i}
                    data-feed-page={`${it.feed}:${it.page.id}`}
                    data-nto-origin={i === startCase ? "" : undefined}
                    style={varsOf(it.c.theme)}
                >
                    {it.c.modules.map((m, j) => (
                        <ModuleView key={j} m={m} first={i === startCase && j === 0} />
                    ))}
                </div>
            ))}
        </div>
    )
}
